import { Story } from '../../types/story';

export const pragueManuscript: Story = {
  id: 'prague-manuscript',
  title: 'The Devil\'s Codex',
  excerpt: 'A rare book authenticator is hired to verify a mysterious 15th-century manuscript in Prague, only to discover that the woman who owns it knows far more about forbidden desires than ancient texts.',
  city: 'Prague',
  publishDate: '2026-01-10',
  readTime: '26 min read',
  tags: ['♂♀', 'mystery', 'occult', 'obsession', 'dark'],
  categories: ['intense'],
  intensity: 3,
  content: `The call came at midnight.

"Mr. Castellano? This is Katerina Novak. I have a book I need authenticated. I'm told you're the best."

I was the best. Twenty years authenticating rare manuscripts, from Vatican archives to private collectors who preferred their acquisitions remain anonymous. I'd handled illuminated gospels, alchemical treatises, grimoires that supposedly summoned demons. None of them had ever worked, of course. They were just books. Expensive, beautiful, historically significant books.

"What's the manuscript?" I asked, already calculating my fee.

"The Codex Diaboli. The Devil's Codex. Fifteenth century, Prague. One of three copies ever made. The other two were burned by the Inquisition."

I sat up in bed. The Devil's Codex was a legend—a manuscript supposedly written by a monk who'd made a pact with Satan, containing rituals for summoning demons, achieving immortality, and other occult nonsense. Most scholars believed it never existed.

"If you have what you claim to have, Ms. Novak, you're sitting on something worth millions."

"I'm not interested in selling it. I'm interested in knowing if it's real. Can you come to Prague?"

"When?"

"Tomorrow. I'll send a car to the airport. First class ticket. Name your price."

I named a price that would have made most clients hang up. She didn't even hesitate.

"Done. I'll send the details within the hour."

She hung up before I could ask any more questions.

---

Prague in November was all grey stone and fog, the kind of city that looked like it had been designed specifically for gothic novels. The car picked me up at the airport—a black Mercedes with tinted windows and a driver who didn't speak—and took me through the winding streets of Malá Strana, past the castle, into a neighborhood of baroque mansions that tourists never saw.

The house was massive, all dark wood and leaded glass windows, the kind of place that had probably been in the same family for centuries. The driver opened my door, gestured toward the entrance, and drove away before I could ask when he'd be back.

The front door opened before I could knock.

She was not what I expected.

Katerina Novak was maybe thirty-five, tall and pale with black hair pulled back in a severe bun, wearing a dark red dress that looked like it cost more than my car. But it was her eyes that caught me—green, intense, the kind that looked like they could see through you.

"Mr. Castellano. Thank you for coming." Her English was perfect, barely accented. "Please, come in."

The interior was exactly what you'd expect—antique furniture, oil paintings of stern ancestors, a grand staircase that curved up into darkness. But there were modern touches too—subtle lighting, a sound system playing something classical and melancholy, fresh flowers that must have cost a fortune in November.

"The manuscript is in my study," she said, leading me down a hallway lined with bookshelves. "I inherited it from my grandfather three months ago. He claimed it was authentic, but he was... eccentric. I need to know the truth."

"Why? If you're not planning to sell it."

She glanced back at me, and there was something in her expression I couldn't read. "Because if it's real, then everything I thought I knew about the world is wrong. And I need to know which world I'm living in."

The study was a book lover's dream—floor-to-ceiling shelves, a massive oak desk, a fireplace with actual flames. And on the desk, under glass, was the manuscript.

I approached it slowly, my heart already racing. Even from a distance, I could tell it was old. The vellum pages were yellowed but intact, the ink still dark, the illuminations—

"Jesus," I breathed.

The illuminations were extraordinary. Not the usual saints and angels you'd find in medieval manuscripts, but demons. Dozens of them, rendered in excruciating detail—horned figures engaged in acts that ranged from violent to obscene, humans writhing in ecstasy or agony, symbols I recognized from grimoires and others I'd never seen.

"May I?" I asked, reaching for the glass cover.

"Please."

I lifted it carefully, pulled on the cotton gloves I always carried, and touched the manuscript for the first time.

The vellum was real—I could tell by the texture, the slight imperfections. The ink was iron gall, consistent with fifteenth-century European manuscripts. The binding was original, the stitching intact. I examined the illuminations under my loupe, looking for signs of modern pigments or techniques.

Nothing. It was either authentic or the best forgery I'd ever seen.

"I'll need to run some tests," I said. "Carbon dating, spectroscopy, chemical analysis of the ink and pigments. But based on initial examination... Ms. Novak, this could be real."

"Call me Katerina." She moved closer, and I caught her scent—something dark and spicy, like incense. "How long will the tests take?"

"A few days. I'll need to take samples—"

"No samples. The manuscript doesn't leave this house. You'll do your work here."

"That's highly irregular—"

"I'll pay triple your fee. You'll stay here, have access to whatever equipment you need, and when you're done, you'll tell me the truth." She was standing very close now, close enough that I could see the flecks of gold in her green eyes. "Do we have an agreement, Mr. Castellano?"

I should have said no. Should have insisted on proper laboratory conditions, on following protocol. But something about her—about the manuscript, about this whole situation—made me want to stay.

"We have an agreement."

---

She set me up in a guest room that was bigger than my apartment in New York, with a four-poster bed and windows overlooking a garden that probably looked beautiful when it wasn't November. A servant—she actually had servants—brought my equipment from the car I hadn't realized was following us.

I spent the first day doing non-invasive tests. Examining the manuscript under different light spectrums, measuring the degradation of the vellum, analyzing the composition of the ink using portable spectroscopy equipment. Everything pointed to authenticity.

Katerina brought me lunch—actual home-cooked food, not the takeout I usually lived on—and watched me work.

"What does it say?" she asked. "The text."

"It's in Latin. Medieval Latin, with some... irregularities. The grammar is wrong in places, like the author was transcribing something from another language." I pointed to a passage. "This section describes a ritual for summoning a demon of lust. It requires blood, candles, and—" I stopped.

"And what?"

"And two participants. Engaging in... specific acts. While reciting the incantation."

"Sex magic," she said calmly. "My grandfather was obsessed with it. He believed that sexual energy could open doorways to other realms."

"Your grandfather sounds like he was interesting at parties."

She smiled, and it transformed her face. "He was insufferable. But he was also brilliant. And he believed this manuscript was the key to something important."

"The key to what?"

"Power. Knowledge. Transcendence. He was never quite clear on the details." She leaned over my shoulder to look at the manuscript, and I felt the warmth of her body against my back. "Do you believe in any of it? The occult, demons, magic?"

"I believe in history. In the power of belief. Whether the rituals actually work..." I shrugged. "I've never seen evidence that they do."

"Maybe you've never tried."

I turned to look at her, and she was closer than I'd realized. Close enough to kiss.

"Are you suggesting we perform a ritual from a fifteenth-century grimoire?" I asked, trying to keep my voice light.

"I'm suggesting that you're here for three more days, and we're both adults, and there's something between us that has nothing to do with manuscripts." Her hand touched my shoulder. "Or am I wrong?"

I should have said yes. Should have maintained professional boundaries. Instead, I stood up, turned to face her fully.

"You're not wrong."

She kissed me.

It was nothing like the tentative first kisses I was used to. It was hungry, demanding, her hands already working on my shirt buttons while her tongue explored my mouth. I pulled her closer, my hands finding the zipper of her dress, and she made a sound of approval.

"Not here," she breathed against my lips. "My bedroom."

We barely made it. Clothes came off in the hallway, a trail of expensive fabric leading to her room. Her bedroom was all dark wood and crimson silk, dominated by a bed that looked like it belonged in a castle.

She pushed me onto it, climbed on top of me, and I forgot about manuscripts and professionalism and everything except the feeling of her skin against mine.

"I want to try something," she said, reaching for her nightstand. She pulled out a book—old, leather-bound—and opened it to a marked page. "This is a translation of one of the rituals. The one you were reading."

"Katerina—"

"I'm not asking you to believe it will work. I'm asking you to indulge me. To see what happens when we combine pleasure with intention." She traced a finger down my chest. "Unless you're afraid?"

I should have been. Should have recognized this for what it was—a client with an unhealthy obsession, a situation that was spiraling out of control. But the way she was looking at me, the way her hand was moving lower, made me reckless.

"What do we need to do?"

She smiled, dark and satisfied. "Exactly what we were already planning to do. But with focus. With intention. With these words."

She began to read in Latin, her voice low and rhythmic, while her hand wrapped around me. The words were familiar—I'd translated them earlier—but hearing them spoken aloud, in this context, made them feel different. Powerful.

She straddled me, positioned herself over me, and as she sank down, she continued reading. I groaned, my hands finding her hips, and she didn't stop—didn't pause—just kept reciting while she moved.

The rhythm of her words matched the rhythm of her body. I was lost in it, in her, in the sensation of her tight and hot around me. She was close, I could tell by the way her voice was getting breathless, the way her movements were becoming more urgent.

"Say it with me," she gasped. "The last line. Say it with me."

She taught me the words, made me repeat them until I had them memorized. And then, as we both approached the edge, we said them together—a phrase in Latin that translated roughly to "open the door, cross the threshold, become one with the infinite."

We came together, her crying out, me groaning the last words of the incantation, and for a moment—just a moment—I could have sworn the room got darker. Colder. Like something had shifted.

Then it passed, and we were just two people, tangled together, breathing hard.

"Did you feel that?" she whispered.

"I felt something."

She climbed off me, lay down beside me, her hand on my chest. "My grandfather used to say that sex was the closest humans could come to touching the divine. That in that moment of release, we're vulnerable enough to let something else in."

"Something else like what?"

"Like knowledge. Like power. Like truth." She propped herself up on one elbow, looked at me with those intense green eyes. "Do you feel different?"

I did, actually. More awake, more alive, like every nerve ending was still firing. But that could have just been good sex.

"I feel like I just had sex with a beautiful woman who's into occult rituals. Which is a first for me."

She laughed, and it was genuine this time. "You're a skeptic. I like that. My grandfather would have hated you."

"I'm a skeptic who's authenticating a manuscript about demon summoning. I contain multitudes."

We lay there in comfortable silence for a while, and I found myself studying the ceiling—ornate plasterwork, painted with more of those demonic figures.

"Your whole house is like this, isn't it?" I said. "Decorated with occult imagery."

"My family has been collecting these things for generations. Books, artifacts, paintings. My grandfather believed we were guardians of forbidden knowledge. That it was our responsibility to keep these things safe."

"Safe from what?"

"From people who would use them. Or from people who would destroy them." She traced patterns on my chest. "He died three months ago. Heart attack. But before he died, he made me promise to find out if the Codex was real. He said if it was, I'd understand everything."

"Understand what?"

"He never said. Just that I'd know when the time came."

---

I stayed for three more days, running every test I could think of. The manuscript was authentic—fifteenth century, Prague, exactly as claimed. The Devil's Codex was real.

And every night, Katerina and I performed another ritual from the book. Each one was different—different words, different positions, different intentions. And each time, I felt that same strange sensation at the moment of climax, like reality was thinning, like something was watching.

On the last night, after we'd finished the final ritual in the manuscript, Katerina lay in my arms and said, "I need to tell you something."

"Okay."

"My grandfather didn't die of a heart attack. He completed the final ritual in the Codex. The one that's supposed to grant immortality. And it killed him."

I sat up, suddenly cold. "What?"

"He believed it would work. Believed he'd transcend death. Instead, his heart just... stopped." She wasn't looking at me. "I found him in his study, the manuscript open in front of him, a smile on his face."

"Jesus, Katerina—"

"But here's the thing. Before he died, he wrote me a note. It said: 'The rituals work, but not the way you think. They don't change the world. They change you. And once you're changed, you can't go back.'"

"What does that mean?"

"I think it means that the magic isn't in the words or the rituals. It's in the intention. In the willingness to open yourself up to something beyond the rational, beyond the safe." She finally looked at me. "I think we've been doing magic all week, Alessandro. Real magic. And I think we're both different now."

She was right. I did feel different. More awake, more aware, like I'd been sleepwalking through my life and had suddenly woken up.

"So what happens now?" I asked.

"Now you go back to New York. You write your authentication report. You tell the world that the Devil's Codex is real." She kissed me, soft this time. "And maybe, if you're feeling brave, you come back. There are more manuscripts. More rituals. More doors to open."

"That sounds dangerous."

"It is. But so is living a safe, small life where nothing ever changes."

---

I left Prague the next morning with my authentication report and a standing invitation to return. The manuscript was real. The rituals... I still didn't know if they were real or if Katerina and I had just convinced ourselves they were.

But I did know one thing: I was different. Changed. Opened.

And three months later, when I got another midnight call from Katerina saying she'd found another manuscript, I didn't hesitate.

I booked the first flight to Prague.

Because some doors, once opened, can't be closed.

And some knowledge, once tasted, becomes an addiction.

The Devil's Codex was real.

And so, I was beginning to suspect, was the magic.`
};
